import React, { useState, useEffect, useCallback, useMemo } from "react";
import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";

import HotelInfoWindow from "../HotelInfoWindow";
import { useTranslation } from "react-i18next";
import HotelMarkerClusterer from "../HotelMarkerClusterer";
import { GoogleMapSecretKey } from "../../../../assets/GlobalVariables";
import { Loader } from "../../../Loader/Loader";
import { debounce } from 'lodash';


// Map styles to match the HotelsRiver theme
const mapStyles = [
  {
    featureType: "poi",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "transit",
    elementType: "labels.icon",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#e9e9e9" }, { lightness: 17 }],
  },
];

// Map container style
const mapContainerStyle = {
  width: "100%",
  height: "100%",
  minHeight: "500px",
  borderRadius: "10px",
  overflow: "hidden",
};

// Map options
const defaultOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: true,
  styles: mapStyles,
  clickableIcons: false,
  gestureHandling: "cooperative",
  maxZoom: 19,
};

const HotelMapView = ({ searchResults, onHotelSelect, isMobile, location ,hoveredId }) => {
  const { t } = useTranslation();
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [hoveredHotel, setHoveredHotel] = useState(null); // New state for hover effect
  const [mapRef, setMapRef] = useState(null);
  const [center, setCenter] = useState({
    lat: location?.latitude || 31.5,
    lng: location?.longitude || 34.75,
  });
  const [currentZoom, setCurrentZoom] = useState(14);
  const [mapLoadAttempts, setMapLoadAttempts] = useState(0);
  const [isBoundsSet, setIsBoundsSet] = useState(false);

  // Use useMemo to filter valid hotels with coordinates once
  const hotelsWithCoords = useMemo(() => {
    return (
      searchResults?.filter(
        (hotel) => hotel.GeoLocation?.Latitude && hotel.GeoLocation?.Longitude
      ) || []
    );
  }, [searchResults]);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GoogleMapSecretKey,
  });

  // Retry loading map if it fails
  useEffect(() => {
    if (loadError && mapLoadAttempts < 3) {
      const timer = setTimeout(() => {
        setMapLoadAttempts((prev) => prev + 1);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [loadError, mapLoadAttempts]);

  // Handle map zoom changes
  const handleZoomChanged = useCallback(() => {
    if (mapRef) {
      setCurrentZoom(mapRef.getZoom());
    }
  }, [mapRef]);

  // Center and zoom the map to fit all hotels - but only once when results change
  useEffect(() => {
    if (hotelsWithCoords.length > 0 && mapRef && !isBoundsSet) {
      if (hotelsWithCoords.length === 1) {
        // If there's only one hotel, center on it with a higher zoom level
        setCenter({
          lat: hotelsWithCoords[0].GeoLocation.Latitude,
          lng: hotelsWithCoords[0].GeoLocation.Longitude,
        });
        mapRef.setZoom(16); // Closer zoom for single hotel
      } else {
        // Fit bounds to show all hotels
        const bounds = new window.google.maps.LatLngBounds();

        // Only process the first 100 markers for bounds to prevent performance issues
        const boundsHotels = hotelsWithCoords.slice(0, 100);

        boundsHotels.forEach((hotel) => {
          bounds.extend({
            lat: hotel.GeoLocation.Latitude,
            lng: hotel.GeoLocation.Longitude,
          });
        });

        mapRef.fitBounds(bounds);

        // If user provided explicit location, use that as center
        if (location?.latitude && location?.longitude) {
          // Wait for bounds to be applied, then set center
          setTimeout(() => {
            setCenter({
              lat: location.latitude,
              lng: location.longitude,
            });
          }, 100);
        }
      }
      setIsBoundsSet(true);
    }
  }, [hotelsWithCoords, mapRef, location, isBoundsSet]);

  // Reset bounds flag when search results change
  useEffect(() => {
    setIsBoundsSet(false);
  }, [searchResults]);

  const onLoad = useCallback(
    (map) => {
      setMapRef(map);
      setCurrentZoom(map.getZoom());

      // Add zoom changed listener
      map.addListener("zoom_changed", handleZoomChanged);
    },
    [handleZoomChanged]
  );

  const handleMarkerClick = useCallback(
    (hotel) => {
      setSelectedHotel(hotel);
      if (onHotelSelect) {
        onHotelSelect(hotel);
      }
    },
    [onHotelSelect]
  );

  // Handle marker mouse enter event
  const handleMarkerMouseOver = useCallback((hotel) => {
    setHoveredHotel(hotel);
  }, []);

  // Handle marker mouse leave event
  const handleMarkerMouseOut = useCallback(() => {
    setHoveredHotel(null);
  }, []);

  const handleInfoWindowClose = useCallback(() => {
    setSelectedHotel(null);
  }, []);


  // Add this useEffect to your HotelMapView component to watch for changes in hoveredId
// Place it after your existing useEffect hooks

// Effect to watch for changes in hoveredId prop
useEffect(() => {
  if (hoveredId && hoveredId !== 0 && mapRef) {
    // Find the hotel that matches the ID
    const hotel = hotelsWithCoords.find(h => h.ID === hoveredId);
    if (hotel && hotel.GeoLocation) {
      // Set it as the hovered hotel to trigger the visual effect
      setHoveredHotel(hotel);
      
      // Create a position object for the map
      const position = {
        lat: hotel.GeoLocation.Latitude,
        lng: hotel.GeoLocation.Longitude
      };
      
      // Pan the map to center on this hotel
      mapRef.panTo(position);
    }
  } else {
    // Reset hovered hotel when no ID is provided
    setHoveredHotel(null);
  }
}, [hoveredId, hotelsWithCoords, mapRef]);


  // Calculate marker options with memoization to prevent re-renders
  const getMarkerOptions = useCallback(
    (hotel) => {
      // Determine marker color based on star rating
      const getMarkerColor = () => {
        const rating = parseInt(hotel.StarRating);
        if (rating >= 5) return "#FF5722"; // Orange for 5-star
        if (rating >= 4) return "#2196F3"; // Blue for 4-star
        if (rating >= 3) return "#4CAF50"; // Green for 3-star
        return "#9E9E9E"; // Grey for lower ratings
      };

      const isSelected = selectedHotel && selectedHotel.ID === hotel.ID;
      const isHovered = hoveredHotel && hoveredHotel.ID === hotel.ID;
      const showPriceLabel = currentZoom >= 14 || isSelected || isHovered; // Show price on hover too

      // Calculate scale based on state
      const scale = isSelected ? 1.5 : isHovered ? 1.4 : 1.3;
      
      // Calculate stroke weight based on state
      const strokeWeight = isSelected ? 2 : isHovered ? 1.5 : 1;
      
      // Apply shadow on hover
      const strokeColor = isSelected || isHovered ? "#000" : "#fff";

      return {
        icon: {
          path: "M 0,0 C -2,-20 -10,-22 -10,-30 A 10,10 0 1,1 10,-30 C 10,-22 2,-20 0,0 z",
          fillColor: getMarkerColor(),
          fillOpacity: isHovered ? 0.9 : 1, // Slight opacity change on hover
          strokeColor: strokeColor,
          strokeWeight: strokeWeight,
          scale: scale,
          // Add shadow on hover
          strokeOpacity: 1,
        },
        label:
          showPriceLabel && hotel.LowestPackagePrice
            ? {
                text: `₪${Math.round(hotel.LowestPackagePrice).toLocaleString()}`,
                color: "#000000",
                fontSize: isHovered ? "13px" : "12px", // Slightly larger text on hover
                fontWeight: isHovered ? "bold" : "bold",
              }
            : null,
        zIndex: isSelected ? 1000 : isHovered ? 999 : 1, // Higher z-index for hovered items
        // Only animate the selected marker if necessary
        animation:
          (isSelected || isHovered) && window.google?.maps?.Animation
            ? window.google.maps.Animation.DROP
            : null,
        // Add hotel data for clustering
        hotelData: hotel,
        // Add cursor style for hover effect
        cursor: "pointer",
      };
    },
    [selectedHotel, hoveredHotel, currentZoom]
  );

  // Only render visible markers for better performance
  const renderMarkers = () => {
    // If too many hotels, limit visible markers based on zoom
    const maxVisibleMarkers =
      currentZoom < 10
        ? 50
        : currentZoom < 12
          ? 100
          : currentZoom < 14
            ? 200
            : 1000;

    // Slice the array to limit markers if needed
    const visibleHotels =
      hotelsWithCoords.length > maxVisibleMarkers
        ? hotelsWithCoords.slice(0, maxVisibleMarkers)
        : hotelsWithCoords;

    return visibleHotels.map((hotel, index) => (
      <Marker
        key={hotel.ID || `hotel-${index}`}
        position={{
          lat: hotel.GeoLocation.Latitude,
          lng: hotel.GeoLocation.Longitude,
        }}
        onClick={() => handleMarkerClick(hotel)}
        onMouseOver={() => handleMarkerMouseOver(hotel)}
        onMouseOut={handleMarkerMouseOut}
        options={getMarkerOptions(hotel)}
      />
    ));
  };

  // Memoize the markers to prevent unnecessary re-renders
  const memoizedMarkers = useMemo(
    () => renderMarkers(),
    [hotelsWithCoords, currentZoom, selectedHotel, hoveredHotel, getMarkerOptions]
  );

  if (loadError) {
    return (
      <div className="tw-p-4">Unable to load maps. Please try again later.</div>
    );
  }

  return isLoaded ? (
    <div
      className="hotel-map-container tw-rounded-lg tw-w-full tw-overflow-hidden"
      style={{
        height: "100%",
      }}
    >
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={center}
        zoom={14}
        onLoad={onLoad}
        options={defaultOptions}
      >
        <HotelMarkerClusterer mapRef={mapRef}>
          {memoizedMarkers}
        </HotelMarkerClusterer>

        {selectedHotel && selectedHotel.GeoLocation && (
          <HotelInfoWindow
            hotel={selectedHotel}
            position={{
              lat: selectedHotel.GeoLocation.Latitude,
              lng: selectedHotel.GeoLocation.Longitude,
            }}
            onClose={handleInfoWindowClose}
          />
        )}
      </GoogleMap>
    </div>
  ) : (
    <div
      style={{
        width: "50%",
        backgroundColor: "#fff",
        borderRadius: "10px",
        position: "relative",
        alignSelf: "flex-end",
      }}
      className="tw-transition-all tw-duration-500"
    >
      <Loader showText />
    </div>
  );
};

export default React.memo(HotelMapView);